import { useAtomValue } from "@effect/atom-react";
import { useNavigate, useParams } from "@tanstack/react-router";
import type { EnvironmentId, ThreadId } from "@amoreg/contracts";
import * as Option from "effect/Option";
import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

import { getClientSettings, useClientSettings } from "../hooks/useSettings";
import { usePrimaryEnvironmentId } from "../state/environments";
import { useActiveOrg } from "../state/orgs";
import { orgUrlSegment } from "../orgRouting";
import { environmentShell } from "../state/shell";
import {
  hasDesktopNotifications,
  hasNotificationSound,
  playNotificationSound,
  setNotificationBadge,
  stopNotificationSounds,
  unlockNotificationAudio,
} from "../threadNotifications";
import { resolveSidebarThreadStatus } from "./Sidebar.logic";
import { toastManager } from "./ui/toast";

export function ThreadNotificationCoordinator() {
  const org = useActiveOrg();
  const environmentId = usePrimaryEnvironmentId();
  // A known membership is required, including on legacy routes. Changing or
  // revoking the organization unmounts the owner of its alerts and baseline.
  return org && environmentId ? (
    <OrganizationNotifications
      key={`${org.id}:${environmentId}`}
      organizationId={org.id}
      environmentId={environmentId}
    />
  ) : null;
}

function OrganizationNotifications({
  organizationId,
  environmentId,
}: {
  organizationId: string;
  environmentId: EnvironmentId;
}) {
  const shell = useAtomValue(environmentShell.stateValueAtom(environmentId));
  const mode = useClientSettings((settings) => settings.notificationMode);
  const inApp = useClientSettings((settings) => settings.inAppNotificationsEnabled);
  const navigate = useNavigate();
  const activeThreadId = useParams({ strict: false, select: (params) => params.threadId });
  const previous = useRef(
    new Map<ThreadId, { attention: string | null; completion: number | null }>(),
  );
  const pending = useRef(new Map<ThreadId, Notification>());
  const toasts = useRef(new Map<ThreadId, ReturnType<typeof toastManager.add>>());
  const alive = useRef(false);
  const currentThreadIds = useRef(new Set<ThreadId>());

  const clearAlerts = useCallback(() => {
    for (const notification of pending.current.values()) notification.close();
    pending.current.clear();
    for (const id of toasts.current.values()) toastManager.close(id);
    toasts.current.clear();
    setNotificationBadge(0);
    stopNotificationSounds();
  }, []);

  useLayoutEffect(() => {
    alive.current = true;
    previous.current.clear();
    const clearSystemAlerts = () => {
      for (const notification of pending.current.values()) notification.close();
      pending.current.clear();
      setNotificationBadge(0);
    };
    window.addEventListener("focus", clearSystemAlerts);
    return () => {
      alive.current = false;
      currentThreadIds.current.clear();
      window.removeEventListener("focus", clearSystemAlerts);
      clearAlerts();
    };
  }, [mode, inApp, clearAlerts]);

  useEffect(() => {
    if (!hasNotificationSound(mode)) return;
    document.addEventListener("pointerdown", unlockNotificationAudio);
    document.addEventListener("keydown", unlockNotificationAudio);
    return () => {
      document.removeEventListener("pointerdown", unlockNotificationAudio);
      document.removeEventListener("keydown", unlockNotificationAudio);
    };
  }, [mode]);

  useEffect(() => {
    if (shell.status !== "live" || Option.isNone(shell.snapshot) || (mode === "off" && !inApp)) {
      previous.current.clear();
      currentThreadIds.current.clear();
      clearAlerts();
      return;
    }
    // In Amoreg the mirrored project id is the organization id. Never observe
    // another organization's threads just because they share a server shell.
    const threads = shell.snapshot.value.threads.filter(
      (thread) => thread.projectId === organizationId,
    );
    currentThreadIds.current = new Set(
      threads.filter((thread) => thread.archivedAt === null).map((thread) => thread.id),
    );
    for (const [id, notification] of pending.current) {
      if (currentThreadIds.current.has(id)) continue;
      notification.close();
      pending.current.delete(id);
    }
    for (const [id, toast] of toasts.current) {
      if (currentThreadIds.current.has(id) && activeThreadId !== id) continue;
      toastManager.close(toast);
      toasts.current.delete(id);
    }
    setNotificationBadge(pending.current.size);
    const next = new Map<ThreadId, { attention: string | null; completion: number | null }>();
    for (const thread of threads) {
      let status = resolveSidebarThreadStatus(thread);
      if (status === "ready" && thread.latestTurn?.state === "error") status = "failed";
      const prior = previous.current.get(thread.id);
      const attention =
        status === "input" || status === "approval" || status === "failed"
          ? `${thread.latestTurn?.turnId ?? ""}:${status}`
          : null;
      const completedAt = Date.parse(thread.latestTurn?.completedAt ?? "");
      const completion =
        status === "ready" &&
        thread.latestTurn?.state === "completed" &&
        Number.isFinite(completedAt)
          ? completedAt
          : (prior?.completion ?? null);
      next.set(thread.id, { attention, completion });
      if (!prior || thread.archivedAt !== null) continue;
      const kind =
        attention && attention !== prior.attention
          ? "input"
          : completion !== null && (prior.completion === null || completion > prior.completion)
            ? "completion"
            : null;
      if (!kind) continue;
      const title =
        kind === "completion"
          ? "Chat completed"
          : status === "approval"
            ? "Approval needed"
            : status === "failed"
              ? "Chat failed"
              : "Input needed";
      const open = () => {
        if (!alive.current || !currentThreadIds.current.has(thread.id)) return;
        void navigate({
          to: "/$org/$threadId",
          params: { org: orgUrlSegment(organizationId), threadId: thread.id },
        });
      };
      if (hasNotificationSound(mode)) {
        void playNotificationSound(
          kind,
          () =>
            alive.current &&
            currentThreadIds.current.has(thread.id) &&
            hasNotificationSound(getClientSettings().notificationMode),
        );
      }
      const focused = document.visibilityState === "visible" && document.hasFocus();
      if (inApp && focused && activeThreadId !== thread.id) {
        const priorToast = toasts.current.get(thread.id);
        if (priorToast !== undefined) toastManager.close(priorToast);
        const id = toastManager.add({
          type: kind === "completion" ? "success" : status === "failed" ? "error" : "warning",
          title,
          description: thread.title,
          data: { hideCopyButton: true },
          actionProps: {
            children: "Open chat",
            onClick: () => {
              toastManager.close(id);
              toasts.current.delete(thread.id);
              open();
            },
          },
        });
        toasts.current.set(thread.id, id);
        continue;
      }
      if (
        !hasDesktopNotifications(mode) ||
        focused ||
        typeof Notification === "undefined" ||
        Notification.permission !== "granted"
      )
        continue;
      try {
        pending.current.get(thread.id)?.close();
        const notification = new Notification(title, {
          body: thread.title,
          tag: `${organizationId}:${thread.id}`,
          silent: true,
        });
        pending.current.set(thread.id, notification);
        setNotificationBadge(pending.current.size);
        notification.addEventListener("close", () => {
          if (pending.current.get(thread.id) !== notification) return;
          pending.current.delete(thread.id);
          setNotificationBadge(pending.current.size);
        });
        notification.addEventListener("click", () => {
          notification.close();
          if (!alive.current) return;
          window.focus();
          open();
        });
      } catch {
        /* Browsers may expose Notification but reject presentation. */
      }
    }
    previous.current = next;
  }, [activeThreadId, environmentId, organizationId, inApp, mode, navigate, shell, clearAlerts]);
  return null;
}
